import type { ReactNode, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { Globe, XCircle } from "lucide-react";
import {
  Button,
  Card,
  EmptyState,
} from "@/components/ui";
import { CopyableReferenceId } from "@/components/common/CopyableReferenceId";
import type { HandshakeSessionDetail } from "@/types";
import { cn } from "@/lib/utils";
import { ChannelBadge, SessionStatusBadge } from "./HandshakeBadges";
import { HandshakeAuditMessageBubble } from "./HandshakeAuditMessageBubble";
import { formatDateTime } from "./config";
import { countHandshakeMessages } from "./sessionUtils";

type AuditPanelVariant = "embedded" | "page" | "flush";

interface HandshakeAuditPanelProps {
  session: HandshakeSessionDetail;
  variant: AuditPanelVariant;
  canCancel: boolean;
  isCancelling: boolean;
  onCancel: () => void;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  messageComposer: ReactNode;
}

export function HandshakeAuditPanel({
  session,
  variant,
  canCancel,
  isCancelling,
  onCancel,
  messagesEndRef,
  messageComposer,
}: HandshakeAuditPanelProps) {
  const { t } = useTranslation("handshakePages");
  const isActive = session.status === "PENDING";
  const counts = countHandshakeMessages(session.messages);
  const isEmbedded = variant === "embedded";
  const isFlush = variant === "flush";

  if (!isEmbedded) {
    const shellClassName = cn(
      "flex min-h-[28rem] flex-col overflow-hidden",
    );
    const header = (
      <header className="shrink-0 space-y-2 border-b border-surface-200/70 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <HandshakeSessionMetaRow session={session} />
            <p className="text-xs tabular-nums text-surface-500">
              {t("started", { date: formatDateTime(session.created_at) })}
              {session.closed_at
                ? ` · ${t("closed")}: ${formatDateTime(session.closed_at)}`
                : ` · ${t("updated")}: ${formatDateTime(session.updated_at)}`}
              {session.status === "CONFIRMED"
                ? ` · ${t("agreementReached")}`
                : null}
              {counts.blocked > 0
                ? ` · ${t("blockedCount", { count: counts.blocked })}`
                : null}
            </p>
          </div>
          {canCancel && isActive ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isCancelling}
              className="shrink-0 text-error-600 hover:bg-error-50 hover:text-error-700"
              leftIcon={<XCircle className="h-4 w-4" />}
            >
              {t("cancelSession")}
            </Button>
          ) : null}
        </div>

        <details className="group text-xs">
          <summary className="cursor-pointer select-none font-medium text-surface-500 hover:text-surface-700">
            {t("references")}
          </summary>
          <div className="mt-2 grid gap-1.5 sm:grid-cols-3">
            <CopyableReferenceId
              label={t("sessionIdShort")}
              value={session.id}
            />
            <CopyableReferenceId
              label={t("bookingId")}
              value={session.booking_id}
            />
            <CopyableReferenceId
              label={t("sellerId")}
              value={session.seller_user_id}
            />
          </div>
        </details>
      </header>
    );

    const trail = (
      <AuditTrail
        variant={variant}
        messages={session.messages}
        counts={counts}
        messagesEndRef={messagesEndRef}
        messageComposer={messageComposer}
      />
    );

    if (isFlush) {
      return (
        <div className={shellClassName}>
          {header}
          {trail}
        </div>
      );
    }

    return (
      <Card className={shellClassName}>
        {header}
        {trail}
      </Card>
    );
  }

  return (
    <div className="border-t border-surface-200/70">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-200/70 bg-surface-50/60 px-4 py-3 dark:bg-surface-100/30">
        <HandshakeSessionMetaRow session={session} />
        <div className="flex flex-wrap items-center gap-2">
          {counts.blocked > 0 ? (
            <span className="rounded-md bg-error-50 px-2 py-0.5 text-xs font-medium text-error-700">
              {t("blockedCount", { count: counts.blocked })}
            </span>
          ) : null}
          {canCancel && isActive ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isCancelling}
              className="text-error-600 hover:bg-error-50 hover:text-error-700"
              leftIcon={<XCircle className="h-4 w-4" />}
            >
              {t("cancelSession")}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="p-4">
        <AuditTrail
          variant={variant}
          messages={session.messages}
          counts={counts}
          messagesEndRef={messagesEndRef}
          messageComposer={messageComposer}
        />
      </div>
    </div>
  );
}

export function HandshakeSessionMetaRow({
  session,
}: {
  session: Pick<
    HandshakeSessionDetail,
    "status" | "customer_channel" | "customer_language"
  >;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SessionStatusBadge status={session.status} size="sm" />
      <ChannelBadge channel={session.customer_channel} size="sm" />
      <span className="flex items-center gap-1 text-xs text-surface-500">
        <Globe className="h-3.5 w-3.5" />
        {session.customer_language.toUpperCase()}
      </span>
    </div>
  );
}

function AuditTrail({
  variant,
  messages,
  counts,
  messagesEndRef,
  messageComposer,
}: {
  variant: AuditPanelVariant;
  messages: HandshakeSessionDetail["messages"];
  counts: ReturnType<typeof countHandshakeMessages>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  messageComposer: ReactNode;
}) {
  const { t } = useTranslation("handshakePages");
  const isEmbedded = variant === "embedded";

  const body =
    messages.length === 0 ? (
      <EmptyState
        size="sm"
        showIcon={false}
        title={t("noMessages")}
        className={isEmbedded ? "py-6" : "py-12"}
      />
    ) : (
      <div className="relative space-y-0">
        {messages.map((message, index) => (
          <div key={message.id} className="relative flex gap-3">
            <div className="flex w-3 shrink-0 flex-col items-center">
              <span
                className={cn(
                  "mt-4 h-2 w-2 rounded-full ring-4 ring-surface-0",
                  message.message_status === "BLOCKED"
                    ? "bg-error-500"
                    : message.agreement_detected
                      ? "bg-success-500"
                      : "bg-surface-300",
                )}
              />
              {index < messages.length - 1 ? (
                <span className="mt-1 w-px flex-1 bg-surface-200" />
              ) : null}
            </div>
            <div className="min-w-0 flex-1 pb-3">
              <HandshakeAuditMessageBubble message={message} />
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    );

  if (isEmbedded) {
    return (
      <div className="rounded-lg border border-surface-200 bg-surface-0">
        <div className="border-b border-surface-200/70 px-4 py-3">
          <p className="text-sm font-semibold text-surface-900">
            {t("auditTrail", { count: counts.total })}
          </p>
        </div>
        <div className="max-h-96 overflow-y-auto p-4">{body}</div>
        {messageComposer}
      </div>
    );
  }

  return (
    <>
      <div className="shrink-0 border-b border-surface-200/70 px-4 py-3 sm:px-5">
        <p className="text-sm font-semibold text-surface-900">
          {t("auditTrail", { count: counts.total })}
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:px-5">
        {body}
      </div>
      {messageComposer}
    </>
  );
}
